import React, { useState, useEffect, useCallback } from 'react';
import config from '../Config';
import { startUserVerification } from '../Config/verificationService';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function User() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchMobile, setSearchMobile] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [verificationType, setVerificationType] = useState('');
  const [roleCounts, setRoleCounts] = useState({
    ALL: null,
    USER: null,
    CONTRACTOR: null,
    WORKER: null,
    ADMIN: null
  });

  const roles = ['USER', 'ADMIN', 'CONTRACTOR', 'WORKER'];

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${config.apiUrl}/api/v1/admin/users?page=${page}&limit=10`;
      
      if (selectedRole) {
        url += `&role=${selectedRole}`;
      }
      if (searchMobile) {
        url += `&mobile=${searchMobile}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data.data);
        setTotalPages(data.meta.totalPages);
      } else {
        setError(data.message || 'Failed to fetch users');
        toast.error(data.message || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Failed to fetch users');
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, selectedRole, searchMobile]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const fetchRoleCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const roleKeys = ['', 'USER', 'CONTRACTOR', 'WORKER', 'ADMIN'];
        const results = await Promise.all(
          roleKeys.map(role =>
            fetch(
              `${config.apiUrl}/api/v1/admin/users?page=1&limit=1${role ? `&role=${role}` : ''}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            ).then(r => r.json())
          )
        );
        const [all, user, contractor, worker, admin] = results;
        setRoleCounts({
          ALL: all?.meta?.total ?? null,
          USER: user?.meta?.total ?? null,
          CONTRACTOR: contractor?.meta?.total ?? null,
          WORKER: worker?.meta?.total ?? null,
          ADMIN: admin?.meta?.total ?? null
        });
      } catch (err) {
        // counts remain null — cards just show '—'
      }
    };
    fetchRoleCounts();
  }, []);

  const handleUpdateRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/users/${selectedUser.mobile}/role`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Role updated successfully');
        setShowRoleModal(false);
        fetchUsers();
      } else {
        toast.error(data.message || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleStartVerification = async () => {
    try {
      const response = await startUserVerification(selectedUser.user_id, verificationType);
      if (response.success) {
        toast.success('Verification started successfully');
        setShowVerificationModal(false);
        fetchUsers();
      } else {
        toast.error('Failed to start verification');
      }
    } catch (err) {
      toast.error('Failed to start verification');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />

      <main className="container mx-auto px-4 py-8">
        {/* Role stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Users', role: 'USER', count: roleCounts.USER, color: 'text-yellow-600' },
            { label: 'Contractors', role: 'CONTRACTOR', count: roleCounts.CONTRACTOR, color: 'text-green-600' },
            { label: 'Workers', role: 'WORKER', count: roleCounts.WORKER, color: 'text-red-600' },
            { label: 'Admins', role: 'ADMIN', count: roleCounts.ADMIN, color: 'text-orange-600' },
          ].map(({ label, role, count, color }) => {
            const isActive = selectedRole === role;
            return (
              <button
                key={label}
                type="button"
                aria-pressed={isActive}
                onClick={() => { setSelectedRole(isActive ? '' : role); setPage(1); }}
                className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${isActive ? 'ring-2 ring-[#EF5E28]' : ''}`}
              >
                <h3 className="text-sm font-medium text-gray-500">{label}</h3>
                <p className={`text-2xl font-bold ${color}`}>
                  {count === null ? '—' : count.toLocaleString()}
                </p>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold mb-6">Users Management</h1>

          <div className="mb-6 flex flex-col sm:flex-row flex-wrap gap-4">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                placeholder="Search by mobile number"
                value={searchMobile}
                onChange={(e) => setSearchMobile(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="w-full sm:w-48 shrink-0">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center">Loading...</div>
          ) : error ? (
            <div className="text-red-500 text-center">{error}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.user_id}>
                        <td className="px-6 py-4 whitespace-nowrap">{user.name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{user.mobile}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{user.role}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{user.email || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setNewRole(user.role);
                              setShowRoleModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Change Role
                          </button>
                          {(user.role === 'CONTRACTOR' || user.role === 'WORKER' || user.role === 'ADMIN') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setVerificationType(user.role);
                                setShowVerificationModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Start Verification
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-center space-x-2">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      {showRoleModal && (
        <div className="fixed inset-0 z-[120] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90dvh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Update User Role</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerificationModal && (
        <div className="fixed inset-0 z-[120] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90dvh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Start User Verification</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Type
              </label>
              <select
                value={verificationType}
                onChange={(e) => setVerificationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="CONTRACTOR">Contractor</option>
                <option value="WORKER">Worker</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                This will start the verification process for <strong>{selectedUser?.name}</strong> as a <strong>{verificationType}</strong>.
              </p>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartVerification}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Start Verification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default User;
